import React, { useEffect, useRef } from 'react'
import noImage from '../../../../images/noimage.png'
import { TextInput } from '../../../../components/form/inputs/TextInput'
import { TextareaInput } from '../../../../components/form/inputs/TextareaInput'
import { RadioInput } from '../../../../components/form/inputs/RadioInput'
import { CheckboxInput } from '../../../../components/form/inputs/CheckboxInput'
import { SelectInput } from '../../../../components/form/inputs/SelectInput'
import { QuillEditor } from '../../../../components/form/inputs/QuillEditor'
import {
  useFieldArray,
  Controller,
  UseFormRegister,
  UseFormWatch,
  UseFormSetValue,
  Control,
  FieldErrors,
} from 'react-hook-form'
import FlatButton from '../../../../components/buttons/flat_button'
import { FormField } from '../../../../components/form'
import { FormErrorMessage } from '../../../../constants/message'
import { useImageUpload } from '../../../../hooks/use_image_upload'
import { CompanyTypeValues } from '../../../../enums/CompanyType'
import { CompanyStageValues } from '../../../../enums/CompanyStage'
import { ServicePurposeValues } from '../../../../enums/ServicePurpose'
import {
  IndustryCategoryParentValues,
  IndustryCategoryChildValues,
  IndustryCategoryCodeRelations,
} from '../../../../enums/IndustryCategory'
import mainBgImage from '../../../../images/main_bg.png'
import { EditFormData } from '../types'

const MAX_SEARCH_INDUSTRY_CATEGORIES = 3 // 探したいタグ(業種カテゴリー)の最大数
const MAX_TOP_IMAGES = 7 // 共創トップ画像の最大数

interface Props {
  register: UseFormRegister<EditFormData>
  watch: UseFormWatch<EditFormData>
  setValue: UseFormSetValue<EditFormData>
  control: Control<EditFormData>
  errors: FieldErrors<EditFormData>
}

const OverviewEditTab: React.FC<Props> = ({
  register,
  watch,
  setValue,
  control,
  errors,
}) => {
  const achievementFileInputRefs = useRef<(HTMLInputElement | null)[]>([])
  const topImageFileInputRefs = useRef<(HTMLInputElement | null)[]>([])
  const responsibleFileInputRef = useRef<HTMLInputElement | null>(null)
  const bannerFileInputRef = useRef<HTMLInputElement | null>(null)
  const { upload } = useImageUpload()

  const {
    fields: topImageFields,
    append: appendTopImage,
    remove: removeTopImage,
  } = useFieldArray({
    control,
    name: 'overview.topImages',
  })

  const {
    fields: achievementFields,
    append: appendAchievement,
    remove: removeAchievement,
  } = useFieldArray({
    control,
    name: 'overview.openInnovationAchievements',
  })

  const { fields: searchIndustryCategoryFields } = useFieldArray({
    control,
    name: 'overview.searchIndustryCategories',
  })

  const formData = watch()

  // 業種カテゴリーの子カテゴリーを取得する関数
  const getChildIndustryCategories = (parentCode: string) => {
    const targetChildCodeStrs = IndustryCategoryCodeRelations[parentCode] ?? []
    return IndustryCategoryChildValues.filter((child) =>
      targetChildCodeStrs.includes(child.code)
    )
  }

  // 共創トップ画像のアップロード処理
  const handleTopImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    const response = await upload(file)

    if (response !== null) {
      const newTopImages = [...watch('overview.topImages')]
      newTopImages[index].imageUrl = response.url
      setValue('overview.topImages', newTopImages)
    }
  }

  // オープンイノベーション実績画像のアップロード処理
  const handleAchievementImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    const response = await upload(file)

    if (response !== null) {
      const newAchievements = [...watch('overview.openInnovationAchievements')]
      newAchievements[index].imageUrl = response.url
      setValue('overview.openInnovationAchievements', newAchievements)
    }
  }

  // 責任者プロフィール画像のアップロード処理
  const handleResponsibleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    const response = await upload(file)

    if (response !== null) {
      setValue('overview.responsibleProfileImg', response.url)
    }
  }

  // バナー画像のアップロード処理
  const handleBannerImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    const response = await upload(file)

    if (response !== null) {
      setValue('overview.bannerImageUrl', response.url)
    }
  }

  useEffect(() => {
    const currentCategories = searchIndustryCategoryFields

    if (
      currentCategories.length === 0 &&
      (!formData.overview.searchIndustryCategories ||
        formData.overview.searchIndustryCategories.length === 0)
    ) {
      // フォームフィールドもサーバーデータも空の場合（新規作成時など）、最大数の空項目を作成して初期化
      setValue(
        'overview.searchIndustryCategories',
        Array(MAX_SEARCH_INDUSTRY_CATEGORIES)
          .fill(null)
          .map(() => ({
            id: null,
            industryParentCode: '',
            industryChildCode: '',
          }))
      )
    } else if (currentCategories.length < MAX_SEARCH_INDUSTRY_CATEGORIES) {
      // 既存データが1-2個の場合、既存データをコピーして最大数になるまで空項目を追加
      const newCategories = [...formData.overview.searchIndustryCategories]
      while (newCategories.length < MAX_SEARCH_INDUSTRY_CATEGORIES) {
        newCategories.push({
          id: null,
          industryParentCode: '',
          industryChildCode: '',
        })
      }

      setValue('overview.searchIndustryCategories', newCategories)
    }
  }, [
    setValue,
    formData.overview.searchIndustryCategories,
    searchIndustryCategoryFields,
  ])

  return (
    <div className="space-y-8">
      {/* バナー画像セクション */}
      <div className="border-b border-black pb-8 pt-6">
        <h3 className="text-xl font-bold mb-6">バナー画像</h3>
        <div className="space-y-4">
          <img
            src={formData.overview.bannerImageUrl || mainBgImage}
            alt="バナー画像"
            className="w-full aspect-[4/1] object-cover"
          />
          <label
            htmlFor="banner-image-upload"
            className="text-main-green p-2 border border-main-green cursor-pointer inline-block text-sm hover:opacity-50 transition-opacity duration-300 rounded-md"
          >
            画像を変更
          </label>
          <input
            id="banner-image-upload"
            ref={bannerFileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleBannerImageUpload(e)}
          />
        </div>
      </div>

      {/* 共創トップ画像セクション */}
      <div className="border-b border-black pb-8">
        <h3 className="text-xl font-bold mb-6">共創トップ画像</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {topImageFields.map((field, index) => (
            <div
              key={field.id}
              className="relative border border-main-darkgray rounded p-4"
            >
              {/* 削除ボタン */}
              <div className="absolute -top-2 -right-2 z-10">
                <FlatButton
                  type="button"
                  onClick={() => removeTopImage(index)}
                  className="bg-main-red"
                >
                  削除
                </FlatButton>
              </div>

              <div className="flex flex-col">
                <img
                  src={field.imageUrl || noImage}
                  alt="共創トップ画像"
                  className="w-48 h-36 rounded-lg object-cover"
                />
                <label
                  htmlFor={`top-image-upload-${index}`}
                  className="text-main-green p-2 border border-main-green cursor-pointer text-sm hover:opacity-50 transition-opacity duration-300 rounded-md text-center mt-4"
                >
                  画像を変更
                </label>
                <input
                  id={`top-image-upload-${index}`}
                  ref={(el) => {
                    topImageFileInputRefs.current[index] = el
                  }}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleTopImageUpload(e, index)}
                />
                <FormField label="表示順" showTag={false}>
                  <TextInput
                    register={register(
                      `overview.topImages.${index}.sortOrder`,
                      {
                        valueAsNumber: true,
                      }
                    )}
                    type="number"
                    className="w-full"
                  />
                </FormField>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6">
          <FlatButton
            type="button"
            onClick={() => {
              appendTopImage({
                id: null,
                imageUrl: '',
                sortOrder: 1,
              })
            }}
            disabled={topImageFields.length >= MAX_TOP_IMAGES}
            className={`${topImageFields.length >= MAX_TOP_IMAGES ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            追加
          </FlatButton>
          {topImageFields.length >= MAX_TOP_IMAGES && (
            <p className="text-main-red mt-2">
              共創トップ画像は{MAX_TOP_IMAGES}枚まで登録できます
            </p>
          )}
        </div>
      </div>

      {/* 企業概要セクション */}
      <div>
        <FormField
          label="企業概要"
          labelClassName="text-xl font-bold mb-3"
          showTag={false}
          error={errors.overview?.companyProfile?.message}
        >
          <TextInput
            register={register('overview.companyProfile', {
              maxLength: {
                value: 250,
                message: FormErrorMessage.maxLength(250),
              },
            })}
            placeholder="企業概要を入力してください"
          />
        </FormField>
      </div>

      {/* 自社特徴セクション */}
      <div className="border-y border-black pb-8 pt-6">
        <FormField
          label="自社特徴"
          labelClassName="text-xl font-bold mb-3"
          showTag={false}
          error={errors.overview?.companyFeature?.message}
        >
          <Controller
            control={control}
            name="overview.companyFeature"
            rules={{
              maxLength: {
                value: 30000,
                message: FormErrorMessage.maxLength(30000),
              },
            }}
            render={({ field }) => (
              <QuillEditor
                value={field.value || ''}
                onChange={(value) => {
                  field.onChange(value)
                  setValue('overview.companyFeature', value)
                }}
                placeholder="自社特徴を入力してください"
              />
            )}
          />
        </FormField>
      </div>

      {/* 提供リソースセクション */}
      <div className="border-b border-black pb-8">
        <FormField
          label="提供リソース"
          labelClassName="text-xl font-bold mb-3"
          showTag={false}
          error={errors.overview?.providedResource?.message}
        >
          <Controller
            control={control}
            name="overview.providedResource"
            rules={{
              maxLength: {
                value: 30000,
                message: FormErrorMessage.maxLength(30000),
              },
            }}
            render={({ field }) => (
              <QuillEditor
                value={field.value || ''}
                onChange={(value) => {
                  field.onChange(value)
                  setValue('overview.providedResource', value)
                }}
                placeholder="提供リソースを入力してください"
              />
            )}
          />
        </FormField>
      </div>

      {/* 主要顧客セクション */}
      <div className="border-b border-main-black pb-8">
        <FormField
          label="主要顧客"
          labelClassName="text-xl font-bold mb-3"
          showTag={false}
          error={errors.overview?.majorCustomer?.message}
        >
          <Controller
            control={control}
            name="overview.majorCustomer"
            rules={{
              maxLength: {
                value: 30000,
                message: FormErrorMessage.maxLength(30000),
              },
            }}
            render={({ field }) => (
              <QuillEditor
                value={field.value || ''}
                onChange={(value) => {
                  field.onChange(value)
                  setValue('overview.majorCustomer', value)
                }}
                placeholder="主要顧客を入力してください"
              />
            )}
          />
        </FormField>
      </div>

      {/* 株主セクション */}
      <div className="border-b border-main-black pb-8">
        <FormField
          label="株主"
          labelClassName="text-xl font-bold mb-3"
          showTag={false}
          error={errors.overview?.shareholder?.message}
        >
          <Controller
            control={control}
            name="overview.shareholder"
            rules={{
              maxLength: {
                value: 30000,
                message: FormErrorMessage.maxLength(30000),
              },
            }}
            render={({ field }) => (
              <QuillEditor
                value={field.value || ''}
                onChange={(value) => {
                  field.onChange(value)
                  setValue('overview.shareholder', value)
                }}
                placeholder="株主情報を入力してください"
              />
            )}
          />
        </FormField>
      </div>

      {/* オープンイノベーション実績セクション */}
      <div className="border-b border-black pb-8">
        <h3 className="text-xl font-bold mb-6">オープンイノベーション実績</h3>
        <div className="space-y-6">
          {achievementFields.map((field, index) => (
            <div
              key={field.id}
              className="relative border border-main-darkgray rounded p-4"
            >
              {/* 削除ボタン */}
              <div className="absolute -top-2 -right-2 z-10">
                <FlatButton
                  type="button"
                  onClick={() => removeAchievement(index)}
                  className="bg-main-red"
                >
                  削除
                </FlatButton>
              </div>

              <div className="flex gap-4">
                {/* 左側の画像 */}
                <div className="flex-shrink-0">
                  <img
                    src={
                      formData.overview.openInnovationAchievements[index]
                        ?.imageUrl || noImage
                    }
                    alt="オープンイノベーション実績画像"
                    className="w-32 h-32 object-cover rounded mb-4"
                  />
                  <label
                    htmlFor={`achievement-image-upload-${index}`}
                    className="text-main-green p-2 border border-main-green cursor-pointer inline-block text-sm hover:opacity-50 transition-opacity duration-300 rounded-md"
                  >
                    画像を変更
                  </label>
                  <input
                    id={`achievement-image-upload-${index}`}
                    ref={(el) => {
                      achievementFileInputRefs.current[index] = el
                    }}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleAchievementImageUpload(e, index)}
                  />
                </div>
                {/* 右側のテキストコンテンツ */}
                <div className="flex-1">
                  <Controller
                    name={`overview.openInnovationAchievements.${index}.content`}
                    control={control}
                    rules={{
                      maxLength: {
                        value: 30000,
                        message: FormErrorMessage.maxLength(30000),
                      },
                    }}
                    render={({ field }) => (
                      <QuillEditor
                        value={field.value || ''}
                        onChange={field.onChange}
                        onBlur={() => {
                          const current = formData.overview
                            .openInnovationAchievements[index] ?? {
                            id: null,
                            imageUrl: '',
                            content: '',
                          }
                          const newAchievements = [
                            ...formData.overview.openInnovationAchievements,
                          ]
                          newAchievements[index] = {
                            ...current,
                            content: field.value,
                          }
                          setValue(
                            'overview.openInnovationAchievements',
                            newAchievements
                          )
                        }}
                        placeholder="実績内容を入力してください"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6">
          <FlatButton
            type="button"
            onClick={() =>
              appendAchievement({
                id: null,
                imageUrl: '',
                content: '',
              })
            }
          >
            追加
          </FlatButton>
        </div>
      </div>

      {/* 本掲載の責任者情報セクション */}
      <div className="border-b border-black pb-8">
        <h3 className="text-xl font-bold mb-6">本掲載の責任者情報</h3>

        <div className="space-y-4">
          <FormField label="プロフィール画像" showTag={false}>
            <img
              src={formData.overview.responsibleProfileImg || noImage}
              alt="プロフィール画像"
              className="w-32 h-32 rounded-lg mb-2"
            />
            <label
              htmlFor="responsible-image-upload"
              className="text-main-green p-2 border border-main-green cursor-pointer inline-block text-sm hover:opacity-50 transition-opacity duration-300 rounded-md"
            >
              画像を変更
            </label>
            <input
              id="responsible-image-upload"
              ref={responsibleFileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleResponsibleImageUpload(e)}
            />
          </FormField>

          <FormField
            label="部署名"
            showTag={false}
            error={errors.overview?.departmentName?.message}
          >
            <TextInput
              register={register('overview.departmentName', {
                maxLength: {
                  value: 250,
                  message: FormErrorMessage.maxLength(250),
                },
              })}
              placeholder="部署名を入力してください"
            />
          </FormField>

          <FormField
            label="役職"
            showTag={false}
            error={errors.overview?.responsiblePosition?.message}
          >
            <TextInput
              register={register('overview.responsiblePosition', {
                maxLength: {
                  value: 250,
                  message: FormErrorMessage.maxLength(250),
                },
              })}
              placeholder="役職を入力してください"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="担当者姓"
              showTag={false}
              error={errors.overview?.responsibleLastName?.message}
            >
              <TextInput
                register={register('overview.responsibleLastName', {
                  maxLength: {
                    value: 50,
                    message: FormErrorMessage.maxLength(50),
                  },
                })}
                placeholder="担当者姓を入力してください"
              />
            </FormField>

            <FormField
              label="担当者名"
              showTag={false}
              error={errors.overview?.responsibleFirstName?.message}
            >
              <TextInput
                register={register('overview.responsibleFirstName', {
                  maxLength: {
                    value: 50,
                    message: FormErrorMessage.maxLength(50),
                  },
                })}
                placeholder="担当者名を入力してください"
              />
            </FormField>
          </div>

          <FormField
            label="部署説明"
            showTag={false}
            error={errors.overview?.departmentDescription?.message}
          >
            <TextareaInput
              register={register('overview.departmentDescription', {
                maxLength: {
                  value: 1000,
                  message: FormErrorMessage.maxLength(1000),
                },
              })}
              placeholder="部署説明を入力してください"
              rows={4}
            />
          </FormField>

          <FormField
            label="自己紹介"
            showTag={false}
            error={errors.overview?.responsibleIntroduction?.message}
          >
            <TextareaInput
              register={register('overview.responsibleIntroduction', {
                maxLength: {
                  value: 1000,
                  message: FormErrorMessage.maxLength(1000),
                },
              })}
              placeholder="自己紹介を入力してください"
              rows={4}
            />
          </FormField>

          <FormField
            label="ミッション"
            showTag={false}
            error={errors.overview?.responsibleMission?.message}
          >
            <TextareaInput
              register={register('overview.responsibleMission', {
                maxLength: {
                  value: 1000,
                  message: FormErrorMessage.maxLength(1000),
                },
              })}
              placeholder="ミッションを入力してください"
              rows={4}
            />
          </FormField>

          <FormField
            label="コメント"
            showTag={false}
            error={errors.overview?.responsibleComment?.message}
          >
            <TextareaInput
              register={register('overview.responsibleComment', {
                maxLength: {
                  value: 1000,
                  message: FormErrorMessage.maxLength(1000),
                },
              })}
              placeholder="コメントを入力してください"
              rows={4}
            />
          </FormField>
        </div>
      </div>

      {/* 探したいタグ */}
      <div className="border-b border-black pb-8">
        <h3 className="text-xl font-bold mb-6">探したいタグ</h3>
        <div className="space-y-6">
          {Array.from(
            { length: MAX_SEARCH_INDUSTRY_CATEGORIES },
            (_, index) => index
          ).map((index) => {
            const currentParentCode =
              formData.overview.searchIndustryCategories?.[index]
                ?.industryParentCode || ''

            return (
              <div key={index} className="grid grid-cols-2 gap-4">
                <FormField label={'業種カテゴリー'} showTag={false}>
                  <SelectInput
                    register={register(
                      `overview.searchIndustryCategories.${index}.industryParentCode`
                    )}
                    options={IndustryCategoryParentValues.map((item) => ({
                      value: item.code,
                      label: item.value,
                    }))}
                  />
                </FormField>
                <FormField label={'　'} showTag={false}>
                  <SelectInput
                    register={register(
                      `overview.searchIndustryCategories.${index}.industryChildCode`
                    )}
                    options={getChildIndustryCategories(currentParentCode).map(
                      (item) => ({
                        value: item.code,
                        label: item.value,
                      })
                    )}
                  />
                </FormField>
                <input
                  type="hidden"
                  {...register(`overview.searchIndustryCategories.${index}.id`)}
                />
              </div>
            )
          })}
        </div>
      </div>

      {/* サービス利用アンケート */}
      <div className="border-b border-black pb-8">
        <h3 className="text-xl font-bold mb-6">サービス利用アンケート</h3>

        <div className="space-y-6">
          <FormField label="自社区分" showTag={false}>
            <CheckboxInput
              register={register('overview.companyTypes')}
              options={CompanyTypeValues.map((item) => ({
                value: item.code,
                label: item.value,
              }))}
              name="overview.companyTypes"
              layout="vertical"
            />
          </FormField>

          <FormField label="企業のステージ" showTag={false}>
            <RadioInput
              register={register('overview.companyStage')}
              options={CompanyStageValues.map((item) => ({
                value: item.code,
                label: item.value,
              }))}
              name="overview.companyStage"
              layout="vertical"
            />
          </FormField>

          <FormField label="サービス利用の目的" showTag={false}>
            <CheckboxInput
              register={register('overview.servicePurposes')}
              options={ServicePurposeValues.map((item) => ({
                value: item.code,
                label: item.value,
              }))}
              name="servicePurposes"
              layout="vertical"
            />
          </FormField>
        </div>
      </div>
    </div>
  )
}

export default OverviewEditTab
